/**
 * Data Citizenship v0 — CSV Import/Export Profiles, Run now, run history.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Download,
  FileSpreadsheet,
  Loader2,
  Play,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { getItemCountState } from '../../../components/collection/types';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import type { FieldMappingEntry } from '../../../lib/fieldMappingRegistry';
import type { CsvCitizenshipProfileMeta } from '../../../lib/dataCitizenship/csvCitizenshipTypes';
import {
  defaultExportEntries,
  defaultImportEntries,
  deleteCsvCitizenshipProfile,
  listCsvCitizenshipProfiles,
  saveCsvCitizenshipProfile,
  type CsvCitizenshipProfile,
} from '../../../lib/dataCitizenship/csvProfilesService';
import {
  listCsvCitizenshipRuns,
  runCsvExportProfile,
  runCsvImportProfile,
} from '../../../lib/dataCitizenship/csvProfileRunner';
import type { CsvCitizenshipRunRecord } from '../../../lib/dataCitizenship/csvCitizenshipTypes';

const SCHEDULE_PRESETS: Array<{ label: string; minutes: number | null }> = [
  { label: 'Manual only', minutes: null },
  { label: 'Hourly', minutes: 60 },
  { label: 'Daily', minutes: 1440 },
  { label: 'Weekly', minutes: 10080 },
];

export default function CsvCitizenshipSettings() {
  const workspaceId = useTenantStore((s) => s.currentWorkspaceId);
  const userId = useUserStore((s) => s.userId);
  const [profiles, setProfiles] = useState<CsvCitizenshipProfile[]>([]);
  const [runs, setRuns] = useState<CsvCitizenshipRunRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState<CsvCitizenshipProfile | null>(null);
  const [creatingKind, setCreatingKind] = useState<'import' | 'export' | null>(null);

  const reload = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const [p, r] = await Promise.all([
        listCsvCitizenshipProfiles(workspaceId),
        listCsvCitizenshipRuns(workspaceId, undefined, 40),
      ]);
      setProfiles(p);
      setRuns(r);
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const countState = getItemCountState(profiles.length);
  const filtered =
    countState === 'many' && query.trim()
      ? profiles.filter((p) =>
          p.set.name.toLowerCase().includes(query.trim().toLowerCase()),
        )
      : profiles;

  async function handleRun(profile: CsvCitizenshipProfile, file?: File) {
    if (!workspaceId) return;
    setBusyId(profile.set.id);
    setMessage(null);
    try {
      if (profile.meta.kind === 'import') {
        let fileText: string | undefined;
        if (file) fileText = await file.text();
        const result = await runCsvImportProfile({
          workspaceId,
          profileId: profile.set.id,
          trigger: 'manual',
          actorUserId: userId,
          fileText,
          fileName: file?.name,
        });
        setMessage(result.error ?? result.summaryLabel);
      } else {
        const result = await runCsvExportProfile({
          workspaceId,
          profileId: profile.set.id,
          trigger: 'manual',
          actorUserId: userId,
        });
        if (result.error) {
          setMessage(result.error);
        } else {
          const blob = new Blob([result.csv], { type: 'text/csv;charset=utf-8;' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `${profile.set.name.replace(/\s+/g, '_')}.csv`;
          a.click();
          URL.revokeObjectURL(url);
          setMessage(`Exported ${result.rowCount} rows (${result.columns.join(', ')})`);
        }
      }
      await reload();
    } finally {
      setBusyId(null);
    }
  }

  async function handleDelete(profileId: string) {
    if (!workspaceId) return;
    if (!confirm('Delete this Spreadsheet sync profile?')) return;
    await deleteCsvCitizenshipProfile(workspaceId, profileId);
    await reload();
  }

  if (!workspaceId) {
    return (
      <div className="p-6 text-sm text-[var(--text-ter)]">Select a workspace to manage CSV profiles.</div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6">
      <PageHeader
        title="Spreadsheet sync"
        subtitle="Import and export items as a spreadsheet. Save how its columns match item fields, run it now or on a schedule, and see every run. Re-importing updates items instead of duplicating them."
        icon={<FileSpreadsheet className="h-4 w-4 text-primary-600" />}
      />

      {message && (
        <div className="border border-slate-blue/20 bg-slate-blue/5 px-4 py-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {message}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setCreatingKind('import')}
          className="inline-flex items-center gap-2 bg-primary-600 text-white px-3 py-2 text-sm font-medium hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" /> Import profile
        </button>
        <button
          type="button"
          onClick={() => setCreatingKind('export')}
          className="inline-flex items-center gap-2 border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 text-sm font-medium hover:bg-[var(--surface)] dark:hover:bg-[var(--surface)]"
        >
          <Plus className="h-4 w-4" /> Export profile
        </button>
      </div>

      {countState === 'many' && (
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search profiles…"
          className="w-full max-w-md border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 text-sm"
        />
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading profiles…
        </div>
      ) : countState === 'zero' ? (
        <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule)] p-6 text-sm text-[var(--text-sec)]">
          No Spreadsheet sync profiles yet. Create an import profile with column mapping, two
          defaults, and an external key — then Run now.
        </div>
      ) : (
        <ul className="space-y-3">
          {filtered.map((p) => (
            <li
              key={p.set.id}
              className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-elevated)] p-4"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">
                      {p.meta.kind}
                    </span>
                    <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                      {p.set.name}
                    </h3>
                  </div>
                  <p className="text-xs text-[var(--text-ter)] mt-1">
                    {p.meta.kind === 'import'
                      ? `Source: ${p.meta.source ?? 'upload'} · key: ${p.meta.externalKeyField ?? 'external_id'} · schedule: ${
                          p.meta.scheduleCadenceMinutes
                            ? `${p.meta.scheduleCadenceMinutes}m`
                            : 'manual'
                        }`
                      : `Destination: ${p.meta.destination ?? 'download'} · schedule: ${
                          p.meta.scheduleCadenceMinutes
                            ? `${p.meta.scheduleCadenceMinutes}m`
                            : 'manual'
                        }`}
                  </p>
                  {p.meta.defaults && Object.keys(p.meta.defaults).length > 0 && (
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">
                      Defaults:{' '}
                      {Object.entries(p.meta.defaults)
                        .map(([k, v]) => `${k}=${v}`)
                        .join(', ')}
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {p.meta.kind === 'import' && (
                    <label className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-2.5 py-1.5 text-xs font-medium cursor-pointer hover:bg-[var(--surface)]">
                      <Upload className="h-3.5 w-3.5" />
                      Upload & run
                      <input
                        type="file"
                        accept=".csv,text/csv"
                        className="hidden"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void handleRun(p, f);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                  <button
                    type="button"
                    disabled={busyId === p.set.id}
                    onClick={() => void handleRun(p)}
                    className="inline-flex items-center gap-1.5 bg-primary-600 text-white px-2.5 py-1.5 text-xs font-medium disabled:opacity-50"
                  >
                    {busyId === p.set.id ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : p.meta.kind === 'export' ? (
                      <Download className="h-3.5 w-3.5" />
                    ) : (
                      <Play className="h-3.5 w-3.5" />
                    )}
                    Run now
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditing(p)}
                    className="border border-[var(--rule)] px-2.5 py-1.5 text-xs font-medium"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleDelete(p.set.id)}
                    className="border border-[var(--coral)] text-[var(--coral)] px-2 py-1.5 text-xs"
                    title="Delete profile"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          Run history
        </h2>
        {runs.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)]">No citizenship runs yet.</p>
        ) : (
          <ul className="space-y-2">
            {runs.map((run) => (
              <li
                key={run.id}
                className="border border-[var(--rule)] dark:border-[var(--rule)] px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap justify-between gap-2">
                  <span className="font-medium">{run.profileName}</span>
                  <span className="text-xs text-[var(--text-ter)] font-mono text-base">
                    {run.trigger} · {new Date(run.startedAt).toLocaleString()}
                  </span>
                </div>
                <p className="text-xs text-[var(--text-sec)] mt-1">
                  new {run.counts.new} · changed {run.counts.changed} · unchanged{' '}
                  {run.counts.unchanged} · queued {run.counts.queued} · missing{' '}
                  {run.counts.missing}
                </p>
                {run.fieldDiffs.length > 0 && (
                  <details className="mt-1">
                    <summary className="text-xs text-primary-700 cursor-pointer">
                      Field-level diffs ({run.fieldDiffs.length})
                    </summary>
                    <ul className="mt-1 space-y-1 text-xs text-[var(--text-sec)]">
                      {run.fieldDiffs.slice(0, 20).map((d, i) => (
                        <li key={i}>
                          {d.externalId ?? d.itemId}: {d.changedFields.join(', ')}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {(creatingKind || editing) && (
        <ProfileEditorModal
          workspaceId={workspaceId}
          kind={editing?.meta.kind ?? creatingKind!}
          initial={editing}
          onClose={() => {
            setCreatingKind(null);
            setEditing(null);
          }}
          onSaved={async () => {
            setCreatingKind(null);
            setEditing(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}

function ProfileEditorModal({
  workspaceId,
  kind,
  initial,
  onClose,
  onSaved,
}: {
  workspaceId: string;
  kind: 'import' | 'export';
  initial: CsvCitizenshipProfile | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(initial?.set.name ?? `CSV ${kind} profile`);
  const [source, setSource] = useState<'upload' | 'url'>(
    initial?.meta.source ?? 'upload',
  );
  const [sourceUrl, setSourceUrl] = useState(initial?.meta.sourceUrl ?? '');
  const [externalKeyField, setExternalKeyField] = useState(
    initial?.meta.externalKeyField ?? 'external_id',
  );
  const [defaultCategory, setDefaultCategory] = useState(
    initial?.meta.defaults?.itemCategory ?? 'feature',
  );
  const [defaultAltitude, setDefaultAltitude] = useState(
    initial?.meta.defaults?.altitudeLevel ?? '3',
  );
  const [cadence, setCadence] = useState<number | null>(
    initial?.meta.scheduleCadenceMinutes ?? null,
  );
  const [destination, setDestination] = useState<'download' | 'url'>(
    initial?.meta.destination ?? 'download',
  );
  const [destinationUrl, setDestinationUrl] = useState(
    initial?.meta.destinationUrl ?? '',
  );
  const [mappingText, setMappingText] = useState(() => {
    if (initial?.set.entries.length) {
      return initial.set.entries
        .map((e) => `${e.externalFieldKey}=${e.internalFieldKey}`)
        .join('\n');
    }
    return kind === 'import'
      ? 'title=title\nexternal_id=external_id\ndescription=description\ncategory=itemCategory\naltitude=altitudeLevel'
      : 'external_id=external_id\ntitle=title\ndescription=description\nitemCategory=itemCategory\naltitudeLevel=altitudeLevel';
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function parseEntries(): FieldMappingEntry[] {
    const lines = mappingText
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    const now = Date.now();
    return lines.map((line, i) => {
      const [left, right] = line.split('=').map((s) => s.trim());
      const external = left || `col_${i}`;
      const internal = right || external;
      return {
        id: `m_${now}_${i}`,
        externalFieldKey: external,
        externalFieldLabel: external,
        internalFieldKey: internal,
        direction: kind === 'import' ? ('import' as const) : ('export' as const),
        enabled: true,
        valueMap: [],
      };
    });
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const entries = parseEntries();
      if (entries.length === 0) {
        setError('Add at least one column mapping (header=field per line).');
        return;
      }
      const meta: CsvCitizenshipProfileMeta =
        kind === 'import'
          ? {
              kind: 'import',
              source,
              sourceUrl: source === 'url' ? sourceUrl.trim() || null : null,
              target: 'items',
              defaults: {
                itemCategory: defaultCategory,
                altitudeLevel: defaultAltitude,
              },
              externalKeyField,
              scheduleCadenceMinutes: cadence,
              lastFileText: initial?.meta.lastFileText ?? null,
              lastFileName: initial?.meta.lastFileName ?? null,
              lastFileStoredAt: initial?.meta.lastFileStoredAt ?? null,
            }
          : {
              kind: 'export',
              sourceFilter: { type: 'active' },
              destination,
              destinationUrl: destination === 'url' ? destinationUrl.trim() || null : null,
              scheduleCadenceMinutes: cadence,
              columnOrder: entries.map((e) => e.internalFieldKey),
            };

      const { error: saveErr } = await saveCsvCitizenshipProfile({
        workspaceId,
        name,
        description: `${kind} spreadsheet sync profile`,
        meta,
        entries:
          kind === 'import'
            ? entries
            : defaultExportEntries(entries.map((e) => e.internalFieldKey)).map((e, i) => ({
                ...e,
                externalFieldKey: entries[i]?.externalFieldKey ?? e.externalFieldKey,
                externalFieldLabel: entries[i]?.externalFieldLabel ?? e.externalFieldLabel,
              })),
        setId: initial?.set.id,
      });
      if (saveErr) {
        setError(saveErr);
        return;
      }
      await onSaved();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg bg-[var(--bg-raised)] dark:bg-[var(--bg-elevated)] shadow-xl max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <h3 className="text-base font-semibold">
          {initial ? 'Edit' : 'Create'} {kind} profile
        </h3>
        <label className="block text-sm">
          <span className="text-xs font-medium text-[var(--text-sec)]">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full border px-3 py-2 text-sm"
          />
        </label>

        {kind === 'import' ? (
          <>
            <label className="block text-sm">
              <span className="text-xs font-medium text-[var(--text-sec)]">File source</span>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as 'upload' | 'url')}
                className="mt-1 w-full border px-3 py-2 text-sm"
              >
                <option value="upload">Upload (stored for re-runs)</option>
                <option value="url">URL (fetched each run)</option>
              </select>
            </label>
            {source === 'url' && (
              <label className="block text-sm">
                <span className="text-xs font-medium text-[var(--text-sec)]">Source URL</span>
                <input
                  value={sourceUrl}
                  onChange={(e) => setSourceUrl(e.target.value)}
                  className="mt-1 w-full border px-3 py-2 text-sm"
                  placeholder="https://…"
                />
              </label>
            )}
            <label className="block text-sm">
              <span className="text-xs font-medium text-[var(--text-sec)]">External key field</span>
              <input
                value={externalKeyField}
                onChange={(e) => setExternalKeyField(e.target.value)}
                className="mt-1 w-full border px-3 py-2 text-sm"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="block text-sm">
                <span className="text-xs font-medium text-[var(--text-sec)]">Default category</span>
                <input
                  value={defaultCategory}
                  onChange={(e) => setDefaultCategory(e.target.value)}
                  className="mt-1 w-full border px-3 py-2 text-sm"
                />
              </label>
              <label className="block text-sm">
                <span className="text-xs font-medium text-[var(--text-sec)]">Default altitude</span>
                <input
                  value={defaultAltitude}
                  onChange={(e) => setDefaultAltitude(e.target.value)}
                  className="mt-1 w-full border px-3 py-2 text-sm"
                />
              </label>
            </div>
          </>
        ) : (
          <>
            <label className="block text-sm">
              <span className="text-xs font-medium text-[var(--text-sec)]">Destination</span>
              <select
                value={destination}
                onChange={(e) => setDestination(e.target.value as 'download' | 'url')}
                className="mt-1 w-full border px-3 py-2 text-sm"
              >
                <option value="download">Download</option>
                <option value="url">POST to URL</option>
              </select>
            </label>
            {destination === 'url' && (
              <label className="block text-sm">
                <span className="text-xs font-medium text-[var(--text-sec)]">Destination URL</span>
                <input
                  value={destinationUrl}
                  onChange={(e) => setDestinationUrl(e.target.value)}
                  className="mt-1 w-full border px-3 py-2 text-sm"
                />
              </label>
            )}
          </>
        )}

        <label className="block text-sm">
          <span className="text-xs font-medium text-[var(--text-sec)]">Schedule</span>
          <select
            value={cadence ?? ''}
            onChange={(e) =>
              setCadence(e.target.value === '' ? null : Number(e.target.value))
            }
            className="mt-1 w-full border px-3 py-2 text-sm"
          >
            {SCHEDULE_PRESETS.map((p) => (
              <option key={String(p.minutes)} value={p.minutes ?? ''}>
                {p.label}
              </option>
            ))}
          </select>
        </label>

        <label className="block text-sm">
          <span className="text-xs font-medium text-[var(--text-sec)]">
            Column mapping (one per line: csvHeader=attribute)
          </span>
          <textarea
            value={mappingText}
            onChange={(e) => setMappingText(e.target.value)}
            rows={6}
            className="mt-1 w-full border px-3 py-2 text-base font-mono"
          />
        </label>

        {error && <p className="text-sm text-[var(--coral)]">{error}</p>}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="border px-3 py-2 text-sm">
            Cancel
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => void save()}
            className="bg-primary-600 text-white px-3 py-2 text-sm font-medium disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save profile'}
          </button>
        </div>
      </div>
    </div>
  );
}

// silence unused import warning for defaultImportEntries in tree-shaking edge cases
void defaultImportEntries;
